import { useShowStore, MULTI_EDIT_COLUMNS } from '@/stores/show';
import { useSettingStore } from '@/stores/setting';
import { useUndoManagerStore } from '@/stores/undoManager';
import { escapeRegExp } from './escapeRegExp';

// When more than one row is selected and the edited row is part of that selection, route the edit through the batched multi-edit path
function applyMultiEditIfSelected(column_cell, value, event, custom_column = undefined) {
	const show_store = useShowStore();
	const selected_ids = show_store.active_show_selected_events || [];
	if (selected_ids.length > 1 && selected_ids.includes(event.event_id)) {
		show_store.applyValueToSelectedEvents({ column_cell, value, custom_column });
		return true;
	}
	return false;
}

export const multiEditBorderClass = (event, column_cell, custom_column = undefined) => {
	const show_store = useShowStore();
	const expected = column_cell === MULTI_EDIT_COLUMNS.CUSTOM ? `${MULTI_EDIT_COLUMNS.CUSTOM}:${custom_column}` : column_cell;
	if (show_store.active_show_selected_column_cell !== expected) {
		return '';
	}
	const edge = show_store.multi_edit_border_map[event.event_id];
	if (edge == undefined) {
		return '';
	}
	let classes = 'relative z-10 border-x-2 border-yellow-500';
	if (edge.top) classes += ' border-t-2';
	if (edge.bottom) classes += ' border-b-2';
	return classes;
};

export function updateDuration(e, event) {
	useShowStore().user_is_editing = false;

	let actual_value = e.target.textContent.replace('s', '');

	// The checks below only return early for the narrow "empty input matching an undefined field"
	// case, not a genuine unchanged value (see the duration_value_without_s exception a few lines
	// down). A plain unedited blur needs its own direct before/after comparison against what was
	// actually displayed, otherwise it falls through to the multi-edit fan-out below.
	if (actual_value == displayDurationText(event).replace('s', '')) {
		return;
	}

	// 1. check 2 conditions:
	// a. the new value is the same as the existing value
	// b. the new value is empty and the existing value is undefined or 0
	if ((event.duration_in_seconds || '0') == actual_value || (actual_value?.trim() == '' && (event.duration_in_seconds == undefined || event.duration_in_seconds == 0))) {
		const duration_value_without_s = event.duration?.trim()?.replace('s', ''); // existing duration value might have string like abc, when the wants to fix it by typing 0, we should allow that and not return.
		// 2. if 1a or 1b is true, check if the duration value without s i.e existing duration value is undefined or empty
		if (duration_value_without_s == undefined || duration_value_without_s?.length > 0 == false) {
			return;
		}
	}

	if (applyMultiEditIfSelected(MULTI_EDIT_COLUMNS.DURATION, actual_value, event)) return;

	const original_events = JSON.parse(JSON.stringify(useShowStore().active_show.events));

	useShowStore().updateEventDuration(event, actual_value);

	const undo_manager = useUndoManagerStore();
	// const affected_events = [event];
	// as now many other events are affected, we need to update all of them
	const post_update_events = JSON.parse(JSON.stringify(useShowStore().active_show.events));
	undo_manager.addAction(post_update_events, original_events);
}

export function callCellValidation(column_cell_name, event) {
	const has_errors = event.errors?.some((error) => error.column == column_cell_name);
	const has_warnings = event.warnings?.some((warning) => warning.column == column_cell_name);

	if (has_errors === false && has_warnings === false) {
		return;
	}

	if (has_errors === true) {
		useShowStore()?.cellValidation(column_cell_name, event.errors);
		return;
	} else {
		useShowStore()?.cellValidation(column_cell_name, event.warnings, 'warning');
	}
}

export function hideValidation() {
	if (useShowStore()?.cell_error == undefined) {
		return;
	}
	useShowStore()?.removeCellValidation();
}

export function selectText(event) {
	useShowStore().user_is_editing = true;

	const selection = window.getSelection();
	const range = document.createRange();

	range.selectNodeContents(event.target);
	if (useShowStore().should_highlight_cell_text == true) {
		selection.removeAllRanges();
		selection.addRange(range);
	}
}

export function updateChannel(e, event) {
	useShowStore().user_is_editing = false;
	if (event.is_address_locked == true) {
		return;
	}

	if (e.target.textContent == event.channel || (e.target.textContent?.trim() == '' && event.channel == undefined)) {
		return;
	}

	if (applyMultiEditIfSelected(MULTI_EDIT_COLUMNS.CHANNEL, e.target.textContent, event)) return;

	const original_events = JSON.parse(JSON.stringify(useShowStore().active_show.events));

	useShowStore().updateEventChannel(event, e.target.textContent);

	const undo_manager = useUndoManagerStore();
	const affected_events = [event];

	undo_manager.addAction(affected_events, original_events);
}

export function updateChannelWithNumber(channel_value, event) {
	if (channel_value == event.channel) {
		return;
	}

	useShowStore().updateEventChannel(event, channel_value);
}

export function updateCue(e, event) {
	useShowStore().user_is_editing = false;

	if (event.is_address_locked == true) {
		return;
	}

	if (e.target.textContent == event.cue || (e.target.textContent?.trim?.() == '' && event.cue == undefined)) {
		return;
	}

	if (applyMultiEditIfSelected(MULTI_EDIT_COLUMNS.CUE, e.target.textContent, event) == true) {
		return;
	}

	const original_events = JSON.parse(JSON.stringify(useShowStore().active_show.events));

	useShowStore()?.updateEventCue(event, e.target.textContent);

	const undo_manager = useUndoManagerStore();
	const affected_events = [event];

	undo_manager.addAction(affected_events, original_events);
}

export function toggleAddressLock(target_events) {
	if (target_events.length == 0) {
		return;
	}
	const show_store = useShowStore();
	if (show_store?.active_show == undefined) {
		return;
	}
	const all_locked = target_events.every((target_event) => target_event.is_address_locked == true);
	const new_locked_value = all_locked == false;
	const original_events = JSON.parse(JSON.stringify(show_store.active_show?.events ?? []));
	// since in JS the objects are passed by reference, we can just update the target events and it will update the events in the show store as well because they are the same objects, we just need to make sure to create a deep copy of the events before updating them to be able to add it to the undo manager.
	for (const target_event of target_events) {
		if (new_locked_value == true) {
			const has_channel = target_event.channel != undefined && target_event.channel !== '';
			const has_cue = target_event.cue != undefined && target_event.cue !== '';
			if (has_channel == false || has_cue == false) {
				continue;
			}
		}
		target_event.is_address_locked = new_locked_value;
	}
	show_store.setChangeMadeToTrue();
	const post_lock_events = JSON.parse(JSON.stringify(show_store.active_show?.events ?? []));
	const undo_manager = useUndoManagerStore();
	undo_manager.addAction(post_lock_events, original_events, (previous_events) => {
		show_store.active_show.events = previous_events;
		show_store.setChangeMadeToTrue();
	});
}

export function updateDMXValue({ new_dmx_value, event }) {
	//Similar to the updateCue but is called from the DMX Value Editor Popup
	useShowStore().user_is_editing = false;

	if ((new_dmx_value == event.cue || (new_dmx_value?.trim?.() == '' && event.cue == undefined)) && (event.dmx_ramp_from_value == undefined || event.dmx_ramp_from_value?.trim?.() == '')) {
		return;
	}

	const original_events = JSON.parse(JSON.stringify(useShowStore().active_show.events));

	useShowStore()?.updateEventCue(event, new_dmx_value);
	useShowStore()?.updateEventDMXRampFromValue({ event, new_dmx_ramp_from_value: '' });

	const undo_manager = useUndoManagerStore();
	const affected_events = [event];

	undo_manager.addAction(affected_events, original_events);
}

export function updateDMXRampValues({ new_dmx_ramp_from_value, new_dmx_ramp_to_value, event }) {
	//Similar to the updateCue but is called from the DMX Value Editor Popup
	useShowStore().user_is_editing = false;

	if ((new_dmx_ramp_to_value == event.cue || (new_dmx_ramp_to_value?.trim?.() == '' && event.cue == undefined)) && (new_dmx_ramp_from_value == event.dmx_ramp_from_value || (new_dmx_ramp_from_value?.trim?.() == '' && event.dmx_ramp_from_value == undefined))) {
		return;
	}

	const original_events = JSON.parse(JSON.stringify(useShowStore().active_show.events));

	useShowStore()?.updateEventCue(event, new_dmx_ramp_to_value);
	useShowStore()?.updateEventDMXRampFromValue({ event, new_dmx_ramp_from_value });

	const undo_manager = useUndoManagerStore();
	const affected_events = [event];

	undo_manager.addAction(affected_events, original_events);
}

export function updateDescription(e, event) {
	useShowStore().user_is_editing = false;

	if (e.target.textContent?.trim() == event.firework_name?.trim()) {
		return;
	}

	if (applyMultiEditIfSelected(MULTI_EDIT_COLUMNS.DESCRIPTION, e.target.textContent?.trim(), event)) return;

	const original_events = JSON.parse(JSON.stringify(useShowStore().active_show.events));

	if (event.my_firework_id != undefined) {
		// useShowStore().unlinkFirework(event, e.target.textContent?.trim(), event);

		useShowStore().unlinkFirework({ event_to_update: event, new_firework_name: e.target.textContent?.trim(), dom_update_cell_event: e, clear_only_description: true });

		const undo_manager = useUndoManagerStore();
		const affected_events = [event];

		undo_manager.addAction(affected_events, original_events);
		return;
	}

	useShowStore()?.updateEventDescription(event, e.target.textContent?.trim());

	const undo_manager = useUndoManagerStore();
	const affected_events = [event];

	undo_manager.addAction(affected_events, original_events);
}

export function updatePreFire(e, event) {
	useShowStore().user_is_editing = false;

	const actual_value = e.target.textContent.replace('s', '');

	// Same gap as updateDuration: the block below only returns for the empty/undefined edge case,
	// not a genuine unchanged value, so a plain unedited blur needs its own direct comparison.
	if (actual_value == displayPrefireText(event).replace('s', '')) {
		return;
	}

	if (actual_value == (event.igniter_pre_fire_in_seconds || '0') || (actual_value?.trim() == '' && (event.igniter_pre_fire_in_seconds == undefined || event.igniter_pre_fire_in_seconds == 0))) {
		if (actual_value?.trim() == '' && event.igniter_pre_fire_in_seconds == undefined) {
			return;
		}
	}

	if (applyMultiEditIfSelected(MULTI_EDIT_COLUMNS.PRE_FIRE, actual_value, event)) return;

	const original_events = JSON.parse(JSON.stringify(useShowStore().active_show.events));

	useShowStore().updateEventPreFire(event, actual_value);

	const undo_manager = useUndoManagerStore();
	const affected_events = [event];

	undo_manager.addAction(affected_events, original_events);
}

export function updateLiftTime(e, event) {
	useShowStore().user_is_editing = false;

	const actual_value = e.target.textContent.replace('s', '');

	// Same gap as updateDuration: the block below only returns for the empty/undefined edge case,
	// not a genuine unchanged value, so a plain unedited blur needs its own direct comparison.
	if (actual_value == displayLiftTimeText(event).replace('s', '')) {
		return;
	}

	if (actual_value == (event.lift_time_in_seconds || '0') || (actual_value?.trim() == '' && (event.lift_time_in_seconds == undefined || event.lift_time_in_seconds == 0))) {
		if (actual_value?.trim() == '' && event.lift_time_in_seconds == undefined) {
			return;
		}
	}

	if (applyMultiEditIfSelected(MULTI_EDIT_COLUMNS.LIFT_TIME, actual_value, event)) return;

	const original_events = JSON.parse(JSON.stringify(useShowStore().active_show.events));

	useShowStore()?.updateEventLiftTime(event, actual_value);

	const undo_manager = useUndoManagerStore();
	const affected_events = [event];

	undo_manager.addAction(affected_events, original_events);
}

export function updatePulseTime(e, event) {
	useShowStore().user_is_editing = false;

	const actual_value = e.target.textContent.replace('s', '');

	if (actual_value == (event.pulse || '0') || (actual_value?.trim() == '' && (event.pulse == undefined || event.pulse == 0))) {
		return;
	}

	if (applyMultiEditIfSelected(MULTI_EDIT_COLUMNS.PULSE, actual_value, event)) return;

	const original_events = JSON.parse(JSON.stringify(useShowStore().active_show.events));

	useShowStore().updateEventPulseTime(event, actual_value);

	const undo_manager = useUndoManagerStore();
	const affected_events = [event];

	undo_manager.addAction(affected_events, original_events);
}

export function updateCustomCell(e, event, custom_column) {
	useShowStore().user_is_editing = false;

	if (e.target.textContent == event.custom_values?.[custom_column] || (e.target.textContent?.trim() == '' && event.custom_values?.[custom_column] == undefined)) {
		return;
	}

	if (applyMultiEditIfSelected(MULTI_EDIT_COLUMNS.CUSTOM, e.target.textContent, event, custom_column)) return;

	const original_events = JSON.parse(JSON.stringify(useShowStore().active_show.events));

	useShowStore()?.updateEventCustomCell(event, custom_column, e.target.textContent);

	const undo_manager = useUndoManagerStore();
	const affected_events = [event];

	undo_manager.addAction(affected_events, original_events);
}

export function updateUniverse(e, event) {
	useShowStore().user_is_editing = false;

	if (e.target.textContent == event.universe) {
		return;
	}

	if (applyMultiEditIfSelected(MULTI_EDIT_COLUMNS.UNIVERSE, e.target.textContent, event)) return;

	const original_events = JSON.parse(JSON.stringify(useShowStore().active_show.events));

	useShowStore().updateEventUniverse(event, e.target.textContent);

	const undo_manager = useUndoManagerStore();
	const affected_events = [event];

	undo_manager.addAction(affected_events, original_events);
}

export function updateComment(e, event) {
	const original_events = JSON.parse(JSON.stringify(useShowStore().active_show.events));

	useShowStore().updateEventComment(event, e.target.textContent);

	const undo_manager = useUndoManagerStore();
	const affected_events = [event];

	undo_manager.addAction(affected_events, original_events);
}

export const isEventTableRowHighlightedForFind = (event) => {
	const all_found_event_ids = useShowStore()?.found_items_to_be_highlighted.map((found_item) => found_item.event.event_id);
	if (all_found_event_ids.includes(event.event_id)) {
		return true;
	}

	return false;
};

export const highlightCellForReplace = (column_cell_name, event) => {
	return useShowStore()?.highlight_column.column_indexes.includes(column_cell_name) && useShowStore()?.highlight_column.event_id == event.event_id;
};

export const rowCellStyles = (column_cell_name, event, text_div_document_id = undefined) => {
	let styles = '';
	if (highlightCellForReplace(column_cell_name, event) == true) {
		styles = 'bg-yellow-400 text-black scroll_to_column_cell';

		const text_div = document.getElementById(text_div_document_id);
		if (useShowStore()?.highlight_column?.word && text_div_document_id != undefined) {
			highlightTextInDiv(text_div, useShowStore()?.highlight_column?.word, useShowStore()?.is_case_sensitivity_enabled_in_find_replace);
		} else {
			removeHighlightFromDiv(text_div);
		}

		return styles;
	} else {
		const text_div = document.getElementById(text_div_document_id);
		removeHighlightFromDiv(text_div);
	}

	if (event.errors?.some((error) => error.column == column_cell_name)) {
		styles = 'cursor-help bg-red-300 text-black';
		return styles;
	} else if (event.warnings?.some((warning) => warning.column == column_cell_name)) {
		styles = 'cursor-help bg-yellow-200 text-black';
	}

	return styles;
};

export const displayDurationText = (event) => {
	return event.duration_in_seconds ? event.duration_in_seconds + 's' : event.duration ? event.duration : '0s';
};

export const highlightCell = (event) => {
	if (event.firework_name == undefined || (typeof event.firework_name == 'string' && event.firework_name?.trim() == '')) {
		return false;
	}
	const events = useShowStore().active_show?.events?.filter((event) => useShowStore().active_show_selected_events.includes(event.event_id)) || [];
	return events.find((active_show_event) => active_show_event.firework_name == event.firework_name && active_show_event.isCOMMENT != true);
};

export const displayPrefireText = (event) => {
	return event.igniter_pre_fire_in_seconds ? event.igniter_pre_fire_in_seconds + 's' : event.igniter_pre_fire ? event.igniter_pre_fire : '0s';
};

export const displayLiftTimeText = (event) => {
	return event.lift_time_in_seconds ? event.lift_time_in_seconds + 's' : event.lift_time ? event.lift_time : '0s';
};

export const displayPulseTimeText = (event) => {
	return event.pulse ? event.pulse + 's' : '0s';
};

function highlightTextInDiv(text_div, word, is_case_sensitive) {
	if (text_div != undefined) {
		const escaped_word = escapeRegExp(word);
		const regex = new RegExp(`(${escaped_word})`, is_case_sensitive ? 'g' : 'gi');
		text_div.innerHTML = text_div.textContent.replace(regex, '<span class="highlight-word-for-find-replace">$1</span>');
	}
}

function removeHighlightFromDiv(text_div) {
	const highlight_span_exists = text_div && text_div.querySelector('.highlight-word-for-find-replace');

	if (highlight_span_exists) {
		text_div.innerHTML = text_div.innerHTML.replace(/<span class="highlight-word-for-find-replace">(.*?)<\/span>/g, '$1');
	}
}

export const highlightCustomColumnForReplace = (column_cell_name, event, text_div_document_id) => {
	if (useShowStore()?.highlight_column?.is_custom_column == true && useShowStore()?.highlight_column?.event_id == event?.event_id && useShowStore()?.highlight_column?.custom_column_name == column_cell_name) {
		const text_div = document.getElementById(text_div_document_id);
		if (useShowStore()?.highlight_column?.word && text_div_document_id != undefined) {
			highlightTextInDiv(text_div, useShowStore()?.highlight_column?.word, useShowStore()?.is_case_sensitivity_enabled_in_find_replace);
		} else {
			removeHighlightFromDiv(text_div);
		}

		return 'bg-yellow-400 scroll_to_column_cell';
	} else {
		const text_div = document.getElementById(text_div_document_id);
		removeHighlightFromDiv(text_div);
	}

	if (event.errors?.some((error) => error.column == column_cell_name) == true) {
		return 'cursor-help bg-red-300 text-black';
	} else if (event.warnings?.some((warning) => warning.column == column_cell_name) == true) {
		return 'cursor-help bg-yellow-200 text-black';
	}

	return '';
};

export const clearSelectedRowsBackgroundColor = () => {
	const show_store = useShowStore();
	const selected_rows = document.querySelectorAll(`[selected_table_row='true']`);
	const row_event_firework_names = [];
	for (const row of selected_rows) {
		const row_event_id = row.getAttribute('event_id');
		const event_index = show_store.active_show.events.findIndex((active_show_event) => active_show_event.event_id == row_event_id);
		row_event_firework_names.push(show_store.active_show.events[event_index].firework_name);
		if (event_index > -1) {
			row.style.backgroundColor = '';
			row.style.color = '';
			const event = show_store.active_show.events[event_index];
			const is_comment = !!event.isCOMMENT;
			const classes_to_add = is_comment ? ['bg-green-300/30', 'text-white'] : [event_index % 2 === 0 ? 'bg-zinc-500' : 'bg-zinc-600'];
			row.classList.add(...classes_to_add);
		}
		row.removeAttribute('selected_table_row');
	}

	for (const event of show_store.active_show.events) {
		const description_cell_element = document.getElementById(`firework_name-${event.event_id}`);
		if (description_cell_element != undefined && row_event_firework_names.includes(event.firework_name)) {
			description_cell_element.style.color = '';
			description_cell_element.style.backgroundColor = '';

			const parent_description_cell_element = document.getElementById(`parent_firework_name-${event.event_id}`);
			if (parent_description_cell_element != undefined) {
				parent_description_cell_element.style.backgroundColor = '';
			}
		}
	}
};

export const clearEventBackgroundColor = (event_id) => {
	const element = Array.from(document.querySelectorAll(`[selected_table_row='true']`)).find((el) => el.getAttribute('event_id') === event_id);

	if (element == undefined) {
		return;
	}

	const show_store = useShowStore();

	const event_index = show_store.active_show.events.findIndex((active_show_event) => active_show_event.event_id == event_id);
	element.style.backgroundColor = '';
	element.style.color = '';
	const event = show_store.active_show.events[event_index];
	const is_comment = !!event.isCOMMENT;
	const classes_to_add = is_comment ? ['bg-green-300/30', 'text-white'] : [event_index % 2 === 0 ? 'bg-zinc-500' : 'bg-zinc-600'];
	element.classList.add(...classes_to_add);
	element.removeAttribute('selected_table_row');

	const description_cell_element = document.getElementById(`firework_name-${event_id}`);
	if (description_cell_element != undefined) {
		description_cell_element.style.color = '';
		description_cell_element.style.backgroundColor = '';

		const parent_description_cell_element = document.getElementById(`parent_firework_name-${event_id}`);
		if (parent_description_cell_element != undefined) {
			parent_description_cell_element.style.backgroundColor = '';
		}
	}
};

export const addSelectedRowBackgroundColor = (event_id) => {
	const table_row = document.getElementById(`table_row-${event_id}`);
	const description_cell = document.getElementById(`firework_name-${event_id}`);

	if (table_row != undefined) {
		table_row.setAttribute('selected_table_row', 'true');
		table_row.setAttribute('event_id', event_id);
		table_row.style.color = 'white';
		table_row.style.backgroundColor = '#5273F1';

		if (description_cell != undefined) {
			const show_store = useShowStore();
			const selected_event = show_store.active_show?.events.find((event) => event.event_id == event_id);
			if (selected_event.firework_name == undefined || (typeof selected_event.firework_name == 'string' && selected_event.firework_name?.trim() == '')) {
				return;
			}

			for (const event of show_store.active_show.events) {
				const description_cell_element = document.getElementById(`firework_name-${event.event_id}`);
				if (description_cell_element != undefined && !event.isCOMMENT && selected_event.firework_name == event.firework_name) {
					description_cell_element.style.color = 'black';
					description_cell_element.style.backgroundColor = '#BFDBFE';

					const parent_description_cell_element = document.getElementById(`parent_firework_name-${event.event_id}`);
					if (parent_description_cell_element != undefined) {
						parent_description_cell_element.style.backgroundColor = '#BFDBFE'; // to solve --> highlighting of matching firework names has a weird border in the latest PR.
					}
				}
			}
		}
	}
};

export const addSelectedRowBackgroundColorToAllEvents = () => {
	const show_store = useShowStore();

	const rows = document.querySelectorAll('[id^="table_row-"]');

	rows.forEach((row, index) => {
		const event = show_store.active_show.events[index];
		row.setAttribute('selected_table_row', 'true');
		row.setAttribute('event_id', event.event_id);
		row.style.color = 'white';
		row.style.backgroundColor = '#5273F1';
	});
};

export const shouldHideDisableFiringGroupColumn = () => {
	const { settings } = useSettingStore() || {};
	const { active_show } = useShowStore() || {};

	if (settings?.should_hide_disable_firing_group_column == true) {
		if (active_show?.events?.some((event) => event?.disable_firing_group != undefined)) {
			return false;
		} else {
			return true;
		}
	} else {
		return false;
	}
};

export const shouldHidePrefireColumn = () => {
	const { settings } = useSettingStore() || {};
	const { active_show } = useShowStore() || {};

	if (settings?.should_hide_prefire_column == true) {
		if (active_show?.events?.some((event) => event?.igniter_pre_fire_in_seconds != undefined && event?.igniter_pre_fire_in_seconds > 0)) {
			return false;
		} else {
			return true;
		}
	} else {
		return false;
	}
};

export const shouldHideLiftTimeColumn = () => {
	const { settings } = useSettingStore() || {};
	const { active_show } = useShowStore() || {};

	if (settings?.should_hide_lift_time_column == true) {
		if (active_show?.events?.some((event) => event?.lift_time_in_seconds != undefined && event?.lift_time_in_seconds > 0)) {
			return false;
		} else {
			return true;
		}
	} else {
		return false;
	}
};

export const shouldHideRelativeTimeColumn = () => {
	const { settings } = useSettingStore() || {};
	const { active_show } = useShowStore() || {};

	if (settings?.should_hide_relative_time_column == true) {
		return true;
	}
	if (active_show?.events?.some((event) => event?.isSTEP == true)) {
		return false;
	}

	return true;
};

export const shouldHidePulseTimeColumn = () => {
	const { settings } = useSettingStore() || {};
	const { active_show } = useShowStore() || {};

	const is_pulse_supported_by_firmware = active_show?.firmware_version >= 6.0;
	if (is_pulse_supported_by_firmware == false) {
		return true;
	}

	if (settings?.should_hide_pulse_time_column == true) {
		// Keep the column visible if any event has a pulse value, including invalid ones, so it can still be seen and fixed
		if (active_show?.events?.some((event) => event?.pulse != undefined && event?.pulse !== '' && event?.pulse != 0)) {
			return false;
		} else {
			return true;
		}
	}

	return false;
};

export const shouldHideDefinedPositionColumn = () => {
	const { settings } = useSettingStore() || {};
	const { active_show } = useShowStore() || {};

	if (settings?.should_hide_defined_position_column == true) {
		if (active_show?.events?.some((event) => event?.defined_position != undefined)) {
			return false;
		} else {
			return true;
		}
	} else {
		return false;
	}
};

export function updateQuantity(e, event) {
	const show_store = useShowStore();
	show_store.user_is_editing = false;

	const raw_value = e.target.textContent.trim();

	// allow clearing the field
	if (raw_value === '') {
		if (event.quantity == undefined) {
			return;
		}
		const original_events = JSON.parse(JSON.stringify(show_store.active_show.events));
		show_store.updateEventQuantity(event, undefined);
		useUndoManagerStore().addAction([event], original_events);
		return;
	}

	const parsed = parseInt(raw_value, 10);

	// reject non-integer, decimals, or values <= 0, revert cell text to last saved value
	if (isNaN(parsed) || parsed <= 0 || String(parsed) !== raw_value) {
		e.target.textContent = event.quantity ?? '';
		return;
	}

	if (parsed === event.quantity) {
		return;
	}

	const original_events = JSON.parse(JSON.stringify(show_store.active_show.events));
	show_store.updateEventQuantity(event, parsed);
	useUndoManagerStore().addAction([event], original_events);
}

export const shouldHideQuantityColumn = () => {
	const { settings } = useSettingStore() || {};
	const { active_show } = useShowStore() || {};

	if (settings?.should_hide_quantity_column == true) {
		// keep visible if any event has a quantity set, so data is never accidentally hidden
		if (active_show?.events?.some((event) => event?.quantity != undefined)) {
			return false;
		} else {
			return true;
		}
	} else {
		return false;
	}
};
